import Promise from 'bluebird';
import _ from 'underscore';

import { constants as C } from '@bingads-webui-campaign-react/image-experience-model';

class ImageSizeValidationError extends Error {
  constructor({
    imageWidth,
    imageHeight,
    minWidth,
    minHeight,
  }) {
    super('SizeValidationError'); // if you change this, please update getMultiAddedImageErrorMessage

    this.imageWidthInternal = imageWidth;
    this.imageHeightInternal = imageHeight;
    this.minWidthInternal = minWidth;
    this.minHeightInternal = minHeight;
  }

  get imageWidth() {
    return this.imageWidthInternal;
  }

  get imageHeight() {
    return this.imageHeightInternal;
  }

  get minWidth() {
    return this.minWidthInternal;
  }

  get minHeight() {
    return this.minHeightInternal;
  }
}

class ImageLoadError extends Error {
  constructor() {
    super('ImageLoadError');
  }
}

const getImageDetailAsync = (url) => {
  const img = new Image();

  let imageLoadedResolve = null;
  let imageLoadedReject = null;
  const imageLoadedPromise = new Promise((resolve, reject) => {
    imageLoadedResolve = resolve;
    imageLoadedReject = reject;
  });

  img.src = url;

  img.onload = () => {
    imageLoadedResolve(img);
  };

  img.onerror = (err) => {
    imageLoadedReject(new ImageLoadError(err));
  };

  return imageLoadedPromise;
};

const validateImageWithDimension = function (
  width,
  height,
  imageType,
  isMultimediaAd = false,
  imageOption = C.defaultImageOption,
  isMMAV2Enabled = false,
  isCombinedMsanAndMmaAspectRatios = false,
  isDisplayAds = false,
  isPortraitImage = false,
  isStoreAppProng = false,
  isStoreAppSpotLight = false,
  isStoreAppTransparentLogo = false,
  isUnifiedAppCampaignWindowsStore = false,
  isLinkedInAd = false,
  isPMaxAspectRatiosV2Enabled = false,
  isBrandKit = false,
  isNewMSANBoostEnabled = false
) {
  const minimumSizeForImage =
    C.getMinimumSizeForImage({
      type: imageType,
      isMultimediaAd,
      imageOption,
      isCombinedMsanAndMmaAspectRatios,
      isMMAV2Enabled,
      isDisplayAds,
      isPortraitImage,
      isBoost200x100ImageEnabled: false,
      isBoostNewAspectRatioSupportEnabled: false,
      isStoreAppProng,
      isStoreAppSpotLight,
      isStoreAppTransparentLogo,
      isLinkedInAd,
      isUnifiedAppCampaignWindowsStore,
      isPMaxAspectRatiosV2Enabled,
      isBrandKit,
      isNewMSANBoostEnabled,
    });

  if (width < minimumSizeForImage[0] ||
    height < minimumSizeForImage[1]) {
    return new ImageSizeValidationError({
      imageWidth: width,
      imageHeight: height,
      minWidth: minimumSizeForImage[0],
      minHeight: minimumSizeForImage[1],
    });
  }

  return null;
};

const validateImageSyncAsyncWithDimensionParams = ({
  imageSource,
  minimumHeight,
  minimumWidth,
}) => {
  const img = new Image();

  let imageLoadedResolve = null;
  let imageLoadedReject = null;
  const imageLoadedPromise = new Promise((resolve, reject) => {
    imageLoadedResolve = resolve;
    imageLoadedReject = reject;
  });

  img.src = imageSource;

  img.onload = () => {
    if (img.naturalWidth < minimumWidth ||
      img.naturalHeight < minimumHeight) {
      imageLoadedReject(new ImageSizeValidationError({
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
        minWidth: minimumWidth,
        minHeight: minimumHeight,
      }));
    } else {
      imageLoadedResolve();
    }
  };

  img.onerror = (err) => {
    imageLoadedReject(new ImageLoadError(err));
  };

  return imageLoadedPromise;
};

const validateImageSizeAsync = function (
  imageSource,
  imageType,
  isMultimediaAd = false,
  imageOption = C.defaultImageOption,
  isMMAV2Enabled = false,
  isCombinedMsanAndMmaAspectRatios = false,
  isDisplayAds = false,
  isPortraitImage = false,
  isStoreAppProng = false,
  isStoreAppSpotLight = false,
  isStoreAppTransparentLogo = false,
  isLinkedInAd = false,
  isPMaxAspectRatiosV2Enabled = false,
  isBrandKit = false
) {
  const minimumSize = C.getMinimumSizeForImage({
    type: imageType,
    isMultimediaAd,
    imageOption,
    isCombinedMsanAndMmaAspectRatios,
    isMMAV2Enabled,
    isDisplayAds,
    isPortraitImage,
    isBoost200x100ImageEnabled: false,
    isBoostNewAspectRatioSupportEnabled: false,
    isStoreAppProng,
    isStoreAppSpotLight,
    isStoreAppTransparentLogo,
    isLinkedInAd,
    isUnifiedAppCampaignWindowsStore: false,
    isPMaxAspectRatiosV2Enabled,
    isBrandKit,
  });

  return validateImageSyncAsyncWithDimensionParams({
    imageSource,
    minimumHeight: minimumSize[1],
    minimumWidth: minimumSize[0],
  });
};

function getImageValidationErrorMessage(e, i18n, isMultiple = false, skipDimension = false) {
  const error = _.result(e, 'message', '');

  // If image failed in upscale, we also treat them as validation error.
  if (e instanceof ImageSizeValidationError || error === 'ImageAIUpScaleFailed') {
    let stringTemplateKey = isMultiple ? 'ImageExperience_MultipleUpload_SizeValidationFailTemplate' : 'ImageExperience_SizeValidationFailTemplate';
    if (skipDimension) {
      stringTemplateKey = isMultiple ? 'ImageExperience_MultipleUpload_SizeValidationFailTemplate_WithoutDimension' : 'ImageExperience_SizeValidationFailTemplate_WithoutDimension';
    }
    /* istanbul ignore else */
    if (e.isDueToResizing) {
      stringTemplateKey = skipDimension ? 'ImageExperience_SizeValidationFailTemplate_WithDetail_WithoutDimension' : 'ImageExperience_SizeValidationFailTemplate_WithDetail';
    }

    return i18n.getString(stringTemplateKey, {
      width: e.minWidth,
      height: e.minHeight,
    });
  }

  if (error) {
    return i18n.getString(`ImageExperience_Error_${error}`) || error;
  }
  return null;
}

function getMultiAddedImageErrorMessage(errors, i18n) {
  const uniqErrors = _.chain(errors).uniq().filter(e => _.isString(e)).value();
  const hasValidateError = _.some(uniqErrors, e => e.indexOf('SizeValidationError') > -1);
  const hasApiError = _.some(uniqErrors, e => e.indexOf('SizeValidationError') === -1);

  if (hasValidateError && hasApiError) {
    return i18n.getString('ImageExperience_Error_MixedError');
  } else if (hasValidateError) {
    return i18n.getString('ImageExperience_Error_ValidationError');
  } else if (hasApiError) {
    return i18n.getString('ImageExperience_Error_APIError');
  }

  return '';
}


const validateMultipleImagesSizesAsync = ({
  imageSources,
  minimumHeight,
  minimumWidth,
}) => {
  const validations = [];

  _.each(imageSources, (imageSource) => {
    validations.push(validateImageSyncAsyncWithDimensionParams({
      imageSource,
      minimumHeight,
      minimumWidth,
    }));
  });

  return Promise.all(validations);
};

export {
  ImageSizeValidationError, ImageLoadError,
  validateImageSizeAsync,
  validateImageWithDimension,
  validateImageSyncAsyncWithDimensionParams,
  validateMultipleImagesSizesAsync,
  getImageValidationErrorMessage,
  getMultiAddedImageErrorMessage,
  getImageDetailAsync,
};
